import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Checkbox,
  Toolbar,
} from '@mui/material';
  import SearchIcon from '@mui/icons-material/Search';
  import { useEffect, useState } from 'react';
import api from '@src/api/clients';
import { Link } from 'react-router-dom';
import { ClientDropdown, DateRangePicker, VerticalDropdown } from '@src/components';
import { formatDateToYMD, getCurrentMonthDates } from '@src/utils/dateUtils';

const sanitizeFilters = (filters = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );

const normalizeNextUrl = (next) => {
  if (!next) return null;
  try {
    const parsed = new URL(next, window.location.origin);
    const relative = `${parsed.pathname}${parsed.search}`;
    let trimmed = relative.startsWith('/') ? relative.slice(1) : relative;
    if (trimmed.startsWith('api/')) {
      trimmed = trimmed.slice(4);
    }
    return trimmed;
  } catch (err) {
    return next.startsWith('/') ? next.slice(1) : next;
  }
};

const fetchAllFilteredLeads = async ({ baseFilters = {}, pageSize = 500 } = {}) => {
  const filters = sanitizeFilters(baseFilters);
  const params = new URLSearchParams({ page: 1, page_size: pageSize });
  Object.entries(filters).forEach(([key, value]) => params.set(key, value));

  const visitedUrls = new Set();
  const rows = [];
  let nextUrl = `dashboard/leads/?${params.toString()}`;
  let expectedCount = null;

  while (nextUrl && !visitedUrls.has(nextUrl)) {
    visitedUrls.add(nextUrl);
    // eslint-disable-next-line no-await-in-loop
    const res = await api.request({ url: nextUrl, method: 'GET' });
    const { data } = res || {};
    const { results = [], next, count } = data || {};
    if (expectedCount === null && typeof count === 'number') {
      expectedCount = count;
    }
    results.forEach((row) => rows.push(row));
    const normalizedNext = normalizeNextUrl(next);
    nextUrl = normalizedNext && !visitedUrls.has(normalizedNext) ? normalizedNext : null;
  }

  return rows;
};
  

export default function LeadListPage() {
  const [defaultStart, defaultEnd] = getCurrentMonthDates();
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    client: '',
    phone: '',
    email: '',
    zip_code: '',
    vertical: '',
    success_post: '',
    date_after: formatDateToYMD(defaultStart),
    date_before: formatDateToYMD(defaultEnd),
  });
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);

  const pageSize = 50;

  const fetchLeads = async (pageParam = page, activeFilters = filters) => {
    setLoading(true);
    try {
      const { phone, email, ...restFilters } = activeFilters;
      const search = phone?.trim() || email?.trim() || '';
      const cleanedFilters = Object.fromEntries(
        Object.entries(restFilters)
          .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
          .filter(([_, v]) => v !== '')
      );
      const params = new URLSearchParams({
        page: pageParam,
        page_size: pageSize,
        ...cleanedFilters,
      });

      if (search) {
        params.set('search', search);
      }

      const res = await api.request({ url: `dashboard/leads/?${params.toString()}`, method: 'GET' });
      setLeads(res.data.results);
      const count = res.data.count || 0;
      setTotalCount(count);
      const currentPageSize = res.data.results?.length || pageSize;
      setTotalPages(Math.max(1, Math.ceil(count / currentPageSize)));
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };
  
    useEffect(() => {
      fetchLeads(page);
    }, [page]);

    const handleSearch = () => {
    const newPage = 1;
    setPage(newPage);
    setSelectAllFiltered(false);
    setSelectedIds([]);
    fetchLeads(newPage);
  };

  const handleInputChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleDateRangeChange = ({ selection }) => {
    const { startDate: nextStart, endDate: nextEnd } = selection;
    setStartDate(nextStart);
    setEndDate(nextEnd);

    const formattedStart = nextStart ? formatDateToYMD(nextStart) : '';
    const formattedEnd = nextEnd ? formatDateToYMD(nextEnd) : '';

    setFilters((prev) => {
      const nextFilters = { ...prev, date_after: formattedStart, date_before: formattedEnd };
      if (page === 1) {
        fetchLeads(1, nextFilters);
      }
      return nextFilters;
    });

    if (page !== 1) {
      setPage(1);
    }
    setSelectAllFiltered(false);
    setSelectedIds([]);
  };

  const postResponseCellSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 260,
  };
  const phoneCellSx = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  const searchFieldBaseSx = { '& .MuiInputBase-root': { height: 40 } };
  const selectFieldBaseSx = {
    '& .MuiInputBase-root': { height: 40, display: 'flex', alignItems: 'center' },
    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 },
  };

  const stringifyValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  };

  const truncate = (value, maxLength = 120) => {
    if (!value) return '-';
    return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
  };

  const formatDateForExport = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const pad = (n) => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const isRowSelected = (id) => selectAllFiltered || selectedIds.includes(id);
  const currentPageIds = leads.map((lead) => lead.id);
  const allOnPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => isRowSelected(id));

  const handleToggleRow = (id) => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds([id]);
      return;
    }
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleToggleAllCurrent = () => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds([]);
      return;
    }

    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      const combined = new Set([...selectedIds, ...currentPageIds]);
      setSelectedIds(Array.from(combined));
    }
  };

  const handleSelectAllFiltered = () => {
    setSelectAllFiltered(true);
    setSelectedIds([]);
  };

  const handleClearSelection = () => {
    setSelectAllFiltered(false);
    setSelectedIds([]);
  };

  const exportLeads = async () => {
    try {
      const exportFields = [
        'id',
        'client_name',
        'webhook_sent_datetime',
        'date_created',
        'date_updated',
        'post_response',
        'response_message',
        'success_post',
        'first_name',
        'last_name',
        'full_name',
        'email',
        'phone',
        'zip_code',
        'city',
        'state',
        'territory',
        'vertical',
        'project_type',
        'product_id',
        'address',
        'street',
        'source',
        'medium',
        'campaign_name',
        'campaign_id',
        'ad_set_name',
        'ad_set_id',
        'placement',
        'ad_name',
        'ad_id',
        'keyword',
        'fbclid',
        'gclid',
        'homeowner',
        'self_book_appointment_date_time',
        'self_book_appointment_date',
        'self_book_time_slot',
        'self_book_notes',
        'self_book_spouse',
        'credit_score',
        'housing_type',
        'portal_id',
        'fbc',
        'fbp',
        'ip_address',
        'user_agent',
        'phone_valid',
        'phone_location',
        'phone_carrier',
        'phone_line_type',
        'flooring_type',
        'flooring_square_feet',
        'flooring_rooms',
        'windows_count',
        'vendor',
        'continent_code',
        'continent_name',
        'country_code',
        'country_name',
        'region_code',
        'region_name',
        'ip_city',
        'ip_zip',
        'zip',
        'ip_latitude',
        'ip_longitude',
        'language_name',
        'time_zone',
        'gmt_offset',
        'time_zone_code',
        'is_daylight_saving',
        'isp',
        'device_type',
        'device_brand',
        'device_name',
        'device_url',
        'device_os_name',
        'device_os_code',
        'device_os_family',
        'device_is_mobile',
        'device_device_type',
        'device_device_brand',
        'device_browser_name',
        'device_browser_version',
        'device_created_at',
        'trusted_form',
        'latitude',
        'longitude',
        'project_timing',
        'reason_for_new_floors',
        'project_stage',
        'flooring_type_other',
        'roof_age',
        'fb_lead_fbtrace_id',
        'fb_completeregistration_fbtrace_id',
        'object_id',
        'processed',
        'client',
        'geo_dma',
      ];

      const dateFields = new Set([
        'webhook_sent_datetime',
        'date_created',
        'date_updated',
        'self_book_appointment_date_time',
        'self_book_appointment_date',
        'device_created_at',
      ]);

      const baseFilters = (() => {
        const { phone, email, ...rest } = filters;
        const search = phone?.trim() || email?.trim() || '';
        const cleaned = sanitizeFilters(rest);
        if (search) {
          cleaned.search = search;
        }
        cleaned.page_size = 500;
        return cleaned;
      })();

      let exportRows = [];

      if (selectAllFiltered) {
        exportRows = await fetchAllFilteredLeads({ baseFilters });
      } else if (selectedIds.length) {
        const currentMap = new Map(leads.map((lead) => [lead.id, lead]));
        const missingIds = selectedIds.filter((id) => !currentMap.has(id));

        if (missingIds.length === 0) {
          exportRows = selectedIds.map((id) => currentMap.get(id)).filter(Boolean);
        } else {
          const allFiltered = await fetchAllFilteredLeads({ baseFilters });
          exportRows = allFiltered.filter((lead) => selectedIds.includes(lead.id));
        }
      } else {
        return;
      }

      if (!exportRows.length) {
        return;
      }

      // Keep fields in the specified order and drop columns that are null/empty for all rows.
      const headers = exportFields.filter((field) =>
        exportRows.some((row) => {
          const value = row ? row[field] : undefined;
          return value !== null && value !== undefined && value !== '';
        })
      );
      if (!headers.length) {
        return;
      }

      const csvRows = [
        headers.join(','),
        ...exportRows.map((row) =>
          headers
            .map((key) => {
              let value = row ? row[key] : '';
              if (value === null || value === undefined) return '';
              if (dateFields.has(key)) {
                value = formatDateForExport(value);
              }
              if (typeof value === 'object') {
                return JSON.stringify(value).replace(/"/g, '""');
              }
              return String(value).replace(/"/g, '""');
            })
            .map((cell) => `"${cell}"`)
            .join(',')
        ),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'leads-export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting leads:', err);
    }
  };

  // Auto-filter when client (primary filter) changes.
  useEffect(() => {
    if (!filters.client) {
      return;
    }
    const newPage = 1;
    setPage(newPage);
    setSelectAllFiltered(false);
    setSelectedIds([]);
    fetchLeads(newPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.client]);

    return (
      <Box p={3}>
        <Card>
          <CardHeader title="Leads" />
          <Divider />
          <CardContent>
            <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2.5,
              mb: 2,
              bgcolor: theme.palette.background.level1 ?? theme.palette.background.default,
              boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
            })}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Filters & search
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Narrow results by client, status, and contact details.
                  </Typography>
                </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Date & status filters
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  alignItems="flex-start"
                >
                  <Box
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      p: 1.25,
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      minWidth: { xs: '100%', sm: 320 },
                      maxWidth: { sm: 420 },
                      bgcolor: theme.palette.background.paper,
                    })}
                  >
                    <Box sx={{ minWidth: { xs: '100%', sm: 260 }, width: '100%' }}>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          Date created
                        </Typography>
                        <DateRangePicker
                          startDate={startDate}
                          endDate={endDate}
                          onChange={handleDateRangeChange}
                          style={{ width: '100%' }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    alignItems="center"
                    sx={{ flex: 1 }}
                  >
                    <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                      <ClientDropdown
                        value={filters.client}
                        onChange={(value) => handleInputChange('client', value)}
                      />
                    </Box>
                    <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                      <VerticalDropdown
                        value={filters.vertical}
                        onChange={(value) => handleInputChange('vertical', value)}
                      />
                    </Box>
                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: '100%', sm: 200 }, ...selectFieldBaseSx }}
                    >
                      <InputLabel>Success Post</InputLabel>
                      <Select
                        value={filters.success_post}
                        onChange={(e) => handleInputChange('success_post', e.target.value)}
                        label="Success Post"
                      >
                        <MenuItem value=""><em>Any</em></MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              </Stack>

              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1.5}
                  alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                >
                  <TextField
                    label="Phone"
                    value={filters.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 200 }, ...searchFieldBaseSx }}
                  />
                  <TextField
                    label="Email"
                    value={filters.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 220 }, ...searchFieldBaseSx }}
                  />
                  <TextField
                    label="ZIP Code"
                    value={filters.zip_code}
                    onChange={(e) => handleInputChange('zip_code', e.target.value)}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 160 }, ...searchFieldBaseSx }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      sx={{ height: 40, minWidth: 96 }}
                    >
                      Search
                    </Button>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            <Toolbar
              sx={{
                px: 0,
                mb: 1,
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {selectAllFiltered
                  ? `All ${totalCount || 0} filtered leads selected`
                  : `${selectedIds.length} selected`}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSelectAllFiltered}
                  disabled={selectAllFiltered || totalCount === 0}
                >
                  Select all filtered
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearSelection}
                  disabled={!selectAllFiltered && selectedIds.length === 0}
                >
                  Clear selection
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={exportLeads}
                  disabled={!selectAllFiltered && selectedIds.length === 0}
                >
                  Export selected
                </Button>
              </Stack>
            </Toolbar>

            <TableContainer component={Paper}>
              <Table size="small">
              <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={!selectAllFiltered && selectedIds.length > 0 && !allOnPageSelected}
                        checked={selectAllFiltered || allOnPageSelected}
                        onChange={handleToggleAllCurrent}
                        inputProps={{ 'aria-label': 'Select all leads on page' }}
                      />
                    </TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Date Created (EST)</TableCell>
                    <TableCell>Date Sent (EST)</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Territory</TableCell>
                    <TableCell>ZIP Code</TableCell>
                    <TableCell>Self-Book Appt</TableCell>
                    <TableCell>Vertical</TableCell>
                    <TableCell>Processed</TableCell>
                    <TableCell>Success Post</TableCell>
                    <TableCell>Post Response</TableCell>
                    <TableCell>Sold Date</TableCell>
                    <TableCell>Net Amount</TableCell>
                    <TableCell>Issue</TableCell>






                </TableRow>
                </TableHead>
                <TableBody sx={{ '& td': { py: 1 } }}>
                    {leads.map((lead) => {
                        const toEST = (utc) =>
                        utc ? new Date(utc).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '-';
                        const backend = lead.related_backend_data?.[0];  // Just the most recent match
                        const postResponseText = stringifyValue(lead.post_response);


                        return (
                        <TableRow key={lead.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isRowSelected(lead.id)}
                                onChange={() => handleToggleRow(lead.id)}
                                inputProps={{ 'aria-label': `Select lead ${lead.id}` }}
                              />
                            </TableCell>
                            <TableCell>{lead.client_name || '-'}</TableCell>
                            <TableCell>{toEST(lead.date_created)}</TableCell>
                            <TableCell>{toEST(lead.webhook_sent_datetime)}</TableCell>
                            <TableCell>
                            <Link to={`/leads/${lead.id}`} style={{ textDecoration: 'none', color: '#2f80ed' }}>
                                {lead.first_name || 'View'}
                            </Link>
                            </TableCell>
                            <TableCell>{lead.last_name || '-'}</TableCell>
                            <TableCell sx={phoneCellSx}>{lead.phone || '-'}</TableCell>
                            <TableCell>{lead.email || '-'}</TableCell>
                            <TableCell>{lead.source || '-'}</TableCell>
                            <TableCell>{lead.territory || '-'}</TableCell>
                            <TableCell>{lead.zip_code || '-'}</TableCell>
                            <TableCell>{lead.self_book_appointment_date_time}</TableCell>
                            <TableCell>{lead.vertical || '-'}</TableCell>
                            <TableCell>{lead.processed ? '✅' : '❌'}</TableCell>
                            <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                            <TableCell sx={postResponseCellSx}>
                              <Tooltip title={postResponseText} enterDelay={400}>
                                <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                                  {truncate(postResponseText, 120)}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{backend?.sold_date || '-'}</TableCell>
                            <TableCell>{backend?.net_amount ? `$${backend.net_amount}` : '-'}</TableCell>
                            <TableCell>{backend?.issue ? '✅' : '❌'}</TableCell>


                        </TableRow>
                        );
                    })}
                    {leads.length === 0 && (
                        <TableRow>
                        <TableCell colSpan={19} align="center">
                            {loading ? 'Loading...' : 'No leads found.'}
                        </TableCell>
                        </TableRow>
                    )}
                    </TableBody>

              </Table>
            </TableContainer>
  
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }
  
